import { useListContext } from 'ra-core';
import { List } from '@/components/admin/list';
import { DataTable } from '@/components/admin/data-table';
import { SearchInput } from '@/components/admin/search-input';
import { DateInput } from '@/components/admin/date-input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useStructureCourante } from '../../fournisseurs/structure-courante';
import { GestesSurUnCours } from './GestesSurUnCours';

type Participant = {
  prenom: string | null;
  anonymise: boolean;
  structureId: string;
  role: 'TUTOR' | 'STUDENT';
};

/** Ce que F-62 enumere de la ligne, et rien de plus. */
type Cours = {
  id: string;
  debutLe: string;
  titre: string;
  participants: Participant[];
  dureeSecondes: number;
  enCours: boolean;
  masque: boolean;
  tableaux: { nombre: number; plafondAtteint: boolean };
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA LISTE DES COURS ET LA SOMME DES MINUTES — F-62.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-62 : « **la liste des cours est l'écran**, une ligne par cours, du plus récent au plus
 * ancien, **ordre non réglable**. »
 *
 * **Ce que la ligne porte** : « date et heure de début, titre, participants **par leur
 * prénom**, **la structure de chaque participant**, durée du cours, état du tableau. »
 *
 * Et le motif de la troisième colonne, qui n'est pas un détail : « **la structure de chaque
 * participant est ce que l'écran d'historique d'une personne ne porte pas**, et une structure
 * en a besoin sur un cours mixte. » Un cours mixte « apparaît dans les deux structures, avec
 * **sa durée entière de chaque côté, sans partage ni prorata** ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LE FILTRE DE DATES DÉCIDE QUELQUE CHOSE, ET C'EST LE SEUL DU PRODUIT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-62 : « **UN FILTRE DE DATES EXISTE, ET LA SOMME DES MINUTES SUIT LE FILTRE.** Sans lui, la
 * somme est un total de toujours et **aucun mois ne se facture**. **C'est la seule surface du
 * produit où un filtre de dates décide quelque chose.** »
 *
 * La somme arrive donc **avec la tranche**, dans `meta`, et non par un second appel : deux
 * appels pourraient rendre une somme et des lignes qui ne se correspondent pas, et personne ne
 * le verrait — le nombre serait simplement faux.
 *
 * **Ce que la somme compte, écrit pour qu'on ne s'en étonne pas.** Elle « compte les cours des
 * comptes anonymisés, et **elle ne baisse jamais rétroactivement** » ; « un cours masqué compte
 * quand même dans la somme de sa structure », alors qu'il a disparu de la liste des
 * participants ; et « la somme des minutes d'une structure éteinte **reste lisible**, par
 * construction : elle est calculée à l'affichage, et les cours portent le rattachement en
 * dur. **C'est ce qui rend la dernière facturation possible après l'arrêt** » (Q546).
 *
 * **Et ce qu'elle n'est pas** : « le produit n'émet **ni facture, ni avoir, ni pièce
 * comptable**. La somme des minutes est **la base d'une facturation qui se fait entièrement
 * hors du produit**, et elle n'est qu'un affichage calculé à l'instant. » L'écran le dit :
 * sans cela, un total affiché à côté d'une période a tout d'un document comptable.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **DEUX DES DIX ÉCRANS RENDUS NE SONT PAS DES ÉCRANS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-62 : « **les tableaux et cours ayant atteint leur plafond sont une marque sur la ligne du
 * cours** ; la fabrication du document d'un cours passé est **un geste sur cette ligne**. » Les
 * deux sont donc ici : une marque en colonne, un geste en fin de ligne. En faire des écrans
 * aurait ajouté deux surfaces à un document qui en compte six.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE LA LISTE NE MONTRE JAMAIS, ET LA BORNE EST DE CLOISONNEMENT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-62 : « **une structure ne voit jamais les cours de ses tuteurs avec des élèves d'une autre
 * structure.** Le contenu d'une séance lui est ouvert **par ses élèves, jamais par ses
 * tuteurs**. » Et le critère 4 : « SI un cours ne porte aucun élève de la structure, ALORS LE
 * SYSTÈME DOIT l'exclure de sa liste, **même si son tuteur lui est rattaché**. »
 *
 * **C'est le serveur qui l'exclut**, et le filet de cloisonnement derrière lui. Refiltrer ici
 * donnerait une seconde règle de périmètre — et la règle côté navigateur est celle qu'on peut
 * retirer avec les outils de développement.
 */
function SommeDesMinutes() {
  // **`meta` est typé `any` par `ra-core`**, et le déstructurer ferait entrer une valeur non
  // sûre dans ce fichier — ce que le lint refuse à juste titre. Le contexte est donc lu **une
  // fois, en entier**, et `meta` est rétréci au seul champ que le fournisseur y met.
  const contexte: unknown = useListContext();
  const meta = (contexte as { meta?: { sommeDesMinutes?: number }; isPending?: boolean }).meta;
  const isPending = (contexte as { isPending?: boolean }).isPending === true;
  const somme = meta?.sommeDesMinutes;
  if (isPending || somme === undefined) return null;

  return (
    <Alert>
      <AlertTitle>{somme} minutes sur la période affichée</AlertTitle>
      <AlertDescription>
        La somme suit le filtre de dates. Elle compte les cours des comptes anonymisés et les
        cours masqués, et ne baisse jamais rétroactivement. Le produit n’émet ni facture ni
        pièce comptable : c’est un affichage calculé à l’instant.
      </AlertDescription>
    </Alert>
  );
}

export function ListeDesCours() {
  const { structureId, selecteurAffiche } = useStructureCourante();

  if (structureId === null) {
    return (
      <main className="p-6">
        <Alert>
          <AlertTitle>Choisissez une structure.</AlertTitle>
          <AlertDescription>
            {selecteurAffiche
              ? 'Les cours se lisent par structure, avec la somme de ses minutes. Prenez une ' +
                'structure dans le sélecteur en tête de la surface.'
              : 'Aucun rattachement actif n’est associé à ce compte.'}
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  return (
    <List
      filter={{ structureId }}
      filters={[
        <SearchInput key="recherche" source="recherche" alwaysOn />,
        // **Les deux bornes sont incluses**, et le contrat le dit sur chaque paramètre.
        <DateInput key="du" source="du" label="Du" alwaysOn />,
        <DateInput key="au" source="au" label="Au" alwaysOn />,
      ]}
      sort={undefined}
      perPage={20}
      // F-66 : l'export des cours est l'un des deux exports, et il vit sur la fiche de la
      // structure. L'exportateur générique de la peau en produirait un troisième, sur la
      // tranche affichée seulement — ce qui serait pire qu'un troisième export : un export
      // partiel qui ressemble à un export complet.
      exporter={false}
    >
      <SommeDesMinutes />
      <DataTable>
        <DataTable.Col<Cours>
          source="debutLe"
          label="Début"
          disableSort
          render={(cours) =>
            new Date(cours.debutLe).toLocaleString('fr-FR')
          }
        />
        <DataTable.Col source="titre" label="Titre" disableSort />
        <DataTable.Col<Cours>
          source="participants"
          label="Participants"
          disableSort
          render={(cours) => (
            <span className="text-sm">
              {cours.participants
                .map((participant) =>
                  // **La désignation anonyme est neutre, sans cause et sans date** — F-62. Ne
                  // pas la distinguer ferait disparaître le participant ; la commenter dirait
                  // pourquoi il l'est, ce que le corpus refuse.
                  participant.anonymise
                    ? '(compte anonymisé)'
                    : (participant.prenom ?? '—'),
                )
                .join(', ')}
            </span>
          )}
        />
        <DataTable.Col<Cours>
          source="participants"
          label="Structures"
          disableSort
          // **Ce que l'historique d'une personne ne porte pas**, et dont une structure a besoin
          // sur un cours mixte : de quelle structure vient chaque participant.
          render={(cours) => (
            <span className="text-muted-foreground text-xs">
              {[...new Set(cours.participants.map((p) => p.structureId))].join(', ')}
            </span>
          )}
        />
        <DataTable.Col<Cours>
          source="dureeSecondes"
          label="Durée"
          disableSort
          render={(cours) =>
            `${Math.round(cours.dureeSecondes / 60)} min`
          }
        />
        <DataTable.Col<Cours>
          source="tableaux"
          label="Tableaux"
          disableSort
          // **La marque de plafond, et non un écran** — F-62.
          render={(cours) => (
            <span className="flex items-center gap-2 text-sm">
              {cours.tableaux.nombre}
              {cours.tableaux.plafondAtteint && (
                <Badge variant="destructive">plafond atteint</Badge>
              )}
            </span>
          )}
        />
        <DataTable.Col<Cours>
          source="masque"
          label="État"
          disableSort
          render={(cours) => (
            <span className="flex gap-1">
              {cours.enCours && <Badge variant="default">en cours</Badge>}
              {cours.masque && <Badge variant="outline">masqué</Badge>}
            </span>
          )}
        />
        <DataTable.Col label="Gestes" disableSort render={() => <GestesSurUnCours />} />
      </DataTable>
    </List>
  );
}
